if (!customElements.get('wls-announcement-bar')) {
  class WLSAnnouncementBar extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        hidden: 'hidden',
        swiperClass: 'js-announcement-bar-slider',
        prevButton: 'js-swiper-btn-prev',
        nextButton: 'js-swiper-btn-next',
        sectionId: `${this.getAttribute('data-section-id')}`,
      };

      this.selectors = {
        swiperElement: this.querySelector(`.${this.classes.swiperClass}`),
        prevButton: this.querySelector(`.${this.classes.prevButton}`),
        nextButton: this.querySelector(`.${this.classes.nextButton}`),
      };

      this.lastScroll = window.scrollY;
      this.countdownIntervals = [];
    }

    /**
     * Lifecycle method called when the element is added to the DOM.
     * It sets up event listeners for each tag to handle tag selection.
     *
     * @returns {void}
     */
    connectedCallback() {
      this.initializeSwiper();
      this.initializeCountdowns();
      
      // Wait for next frame to ensure element is fully rendered
      requestAnimationFrame(() => {
        this.setAnnouncementBarHeight();
      });
      
      window.matchMedia('(max-width: 990px)').addEventListener('change', () => {
        requestAnimationFrame(() => {
          this.setAnnouncementBarHeight();
        });
      });
      window.addEventListener('scroll', this.handleScroll.bind(this));
    }

    /**
     * Initialize Swiper.
     *
     * @returns {void}
     */
    initializeSwiper() {
      const settingsElement = document.getElementById(`CarouselSettings-${this.classes.sectionId}`);
      const settings = JSON.parse(settingsElement.textContent);

      this.swiper = new Swiper(this.selectors.swiperElement, {
        loop: settings.loop,
        spaceBetween: 12,
        slidesPerView: 1,
        pagination: false,
        autoplay: {
          delay: settings.animationSpeed || 3000,
          enabled: settings.autoplay,
        },

        navigation: {
          prevEl: this.selectors.prevButton,
          nextEl: this.selectors.nextButton,
          enabled: true,
        },
      });

      if (!this.swiper.isLocked) {
        this.selectors.prevButton.classList.remove(this.classes.hidden);
        this.selectors.nextButton.classList.remove(this.classes.hidden);
      } else {
        this.selectors.prevButton.classList.add(this.classes.hidden);
        this.selectors.nextButton.classList.add(this.classes.hidden);
      }
    }

    /**
     * Initialize all countdown timers
     *
     * @returns {void}
     */
    initializeCountdowns() {
      const countdownSlides = this.querySelectorAll('.js-countdown-slide');
      
      countdownSlides.forEach((slide) => {
        const dateStr = slide.getAttribute('data-countdown-date');
        const timeStr = slide.getAttribute('data-countdown-time') || '00:00';
        
        if (!dateStr) return;

        // Parse date and time
        const dateParts = dateStr.split('-');
        const [month, day, year] = dateParts.map(Number);
        const timeParts = timeStr.split(':');
        const [hours, minutes] = timeParts.map(Number);
        
        const targetDate = new Date(year, month - 1, day, hours || 0, minutes || 0, 0);
        
        const daysEl = slide.querySelector('.wls-countdown-days');
        const hoursEl = slide.querySelector('.wls-countdown-hours');
        const minutesEl = slide.querySelector('.wls-countdown-minutes');
        const secondsEl = slide.querySelector('.wls-countdown-seconds');
        
        if (!daysEl || !hoursEl || !minutesEl || !secondsEl) return;

        // Update immediately
        this.updateCountdown(targetDate, daysEl, hoursEl, minutesEl, secondsEl, slide);

        // Update every second
        const interval = setInterval(() => {
          const shouldContinue = this.updateCountdown(targetDate, daysEl, hoursEl, minutesEl, secondsEl, slide);
          if (!shouldContinue) {
            clearInterval(interval);
          }
        }, 1000);

        this.countdownIntervals.push(interval);
      });
    }

    /**
     * Update countdown display
     *
     * @param {Date} targetDate - The target date and time
     * @param {HTMLElement} daysEl - Days element
     * @param {HTMLElement} hoursEl - Hours element
     * @param {HTMLElement} minutesEl - Minutes element
     * @param {HTMLElement} secondsEl - Seconds element
     * @param {HTMLElement} slide - The slide container
     * @returns {boolean} - Returns false if countdown has expired
     */
    updateCountdown(targetDate, daysEl, hoursEl, minutesEl, secondsEl, slide) {
      const now = new Date();
      const difference = targetDate - now;

      if (difference <= 0) {
        daysEl.textContent = '00';
        hoursEl.textContent = '00';
        minutesEl.textContent = '00';
        secondsEl.textContent = '00';
        slide.classList.add('wls-countdown-expired');
        return false;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      daysEl.textContent = String(days).padStart(2, '0');
      hoursEl.textContent = String(hours).padStart(2, '0');
      minutesEl.textContent = String(minutes).padStart(2, '0');
      secondsEl.textContent = String(seconds).padStart(2, '0');

      return true;
    }

    handleScroll() {
      const currentScroll = window.scrollY;
      // Always use the actual height, regardless of scroll position
      const height = this.offsetHeight || this.getBoundingClientRect().height || 0;
      document.documentElement.style.setProperty('--wls-announcement-bar-height', `${height}px`);
      this.lastScroll = currentScroll;
    }

    setAnnouncementBarHeight() {
      // Use getBoundingClientRect for more accurate height measurement
      const height = this.offsetHeight || this.getBoundingClientRect().height || 0;
      document.documentElement.style.setProperty('--wls-announcement-bar-height', `${height}px`);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      // Clear all countdown intervals
      this.countdownIntervals.forEach(interval => clearInterval(interval));
      this.countdownIntervals = [];
    }
  }

  window.customElements.define('wls-announcement-bar', WLSAnnouncementBar);
}